import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { track } from '@/lib/analytics';
import { FiArrowLeft } from 'react-icons/fi';
import { Card, Button } from '@/components';
import { apiJson, ApiError, extractApiErrorMessage } from '@/lib/api';
import { useHouseholds } from '@/lib/household-context';
import { formatDateLocal } from '@/lib/utils';
import type {
  UserResponse,
  HouseholdResponse,
  CustodySchedule,
  CalendarResponse,
} from '@/lib/types';
import { SCHEDULE_TYPE_NAMES } from '@/lib/types';

function AddExceptionPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();

  // Track screen view
  useEffect(() => {
    track('Add Custody Exception Screen');
  }, []);

  // Initialize date from URL param or today
  const initialDate = search.date ?? formatDateLocal(new Date());

  const [selectedSchedule, setSelectedSchedule] = useState<number | null>(null);
  const [exceptionDate, setExceptionDate] = useState(initialDate);
  const [requestType, setRequestType] = useState<'SWAP' | 'TIME_CHANGE'>('SWAP');
  const [selectedAssignedParent, setSelectedAssignedParent] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [pickupTime, setPickupTime] = useState<string>('');
  const [dropoffTime, setDropoffTime] = useState<string>('');

  // Fetch user
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
  });

  // Fetch households
  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        return await apiJson<HouseholdResponse[]>(getToken, '/v1/households');
      } catch (err) {
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          return [] as HouseholdResponse[];
        }
        throw err;
      }
    },
  });

  // Fetch schedules
  const schedulesQuery = useQuery({
    queryKey: ['custody-schedules'],
    queryFn: async () => {
      try {
        return await apiJson<CustodySchedule[]>(getToken, '/v1/schedules');
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
          return [] as CustodySchedule[];
        }
        throw err;
      }
    },
  });

  const currentUser = userQuery.data;
  const { currentHouseholdId } = useHouseholds();
  const households = householdsQuery.data ?? [];
  const firstHousehold =
    households.find((h) => h.id === currentHouseholdId) ?? households[0];
  const schedules = useMemo(() => schedulesQuery.data ?? [], [schedulesQuery.data]);
  const parents = firstHousehold?.members ?? [];

  // Get beneficiary info for each schedule
  const schedulesWithBeneficiaries = useMemo(() => {
    return schedules.map((schedule) => {
      const beneficiary = firstHousehold?.beneficiaries.find(
        (b) => b.beneficiary_id === schedule.beneficiary_id
      );
      return {
        ...schedule,
        beneficiaryName: beneficiary?.name ?? 'Unknown',
      };
    });
  }, [schedules, firstHousehold]);

  // Get who normally has custody on the selected date
  const selectedScheduleData = schedulesWithBeneficiaries.find((s) => s.id === selectedSchedule);
  const beneficiaryId = selectedScheduleData?.beneficiary_id;

  const normalCustodyQuery = useQuery({
    queryKey: ['calendar', exceptionDate, exceptionDate, selectedSchedule, beneficiaryId],
    queryFn: async () => {
      if (!beneficiaryId) return null;
      try {
        return await apiJson<CalendarResponse>(
          getToken,
          `/v1/calendar?start_date=${exceptionDate}&end_date=${exceptionDate}&beneficiary_id=${beneficiaryId}`
        );
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!selectedSchedule && !!beneficiaryId,
  });

  const calendarDay = normalCustodyQuery.data?.calendar?.[0];
  const normalAssignment = calendarDay?.assignments.find(
    (a) => a.beneficiary_id === beneficiaryId && !a.is_exception
  );
  const normalCustodyUserId = normalAssignment?.assigned_user_id;
  const normalCustodyParent = normalCustodyUserId
    ? parents.find((p) => p.user_id === normalCustodyUserId)
    : null;
  const transitionType = normalAssignment?.transition_type ?? null;
  const usesTransitionTimes = !!(
    selectedScheduleData?.transition_times &&
    selectedScheduleData.transition_times.length > 0
  );

  const getParentName = (userId: number): string => {
    if (userId === currentUser?.id) return 'You';
    const member = parents.find((m) => m.user_id === userId);
    if (!member) return `Parent ${userId}`;
    if (member.first_name) return member.first_name;
    return `Parent ${userId}`;
  };

  // Create exception mutation
  const createExceptionMutation = useMutation({
    mutationFn: async (vars: {
      assignedUserId: number;
      exceptionType: 'SWAP' | 'TIME_CHANGE';
      pickupTime: string;
      dropoffTime: string;
    }) => {
      const body: Record<string, unknown> = {
        exception_date: exceptionDate,
        assigned_user_id: vars.assignedUserId,
        exception_type: vars.exceptionType,
        reason: reason.trim() || null,
      };

      // Add times if set (format HH:MM to HH:MM:SS for backend)
      if (vars.pickupTime) {
        body.pickup_time = `${vars.pickupTime}:00`;
      }
      if (vars.dropoffTime) {
        body.dropoff_time = `${vars.dropoffTime}:00`;
      }

      return apiJson(getToken, `/v1/schedules/${selectedSchedule}/exceptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custody-exceptions'] });
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
      navigate({ to: '/app/calendar' });
    },
    onError: (err: unknown) => {
      alert(extractApiErrorMessage(err, 'Could not create exception.'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSchedule) {
      alert('Please select a custody schedule.');
      return;
    }

    if (requestType === 'TIME_CHANGE') {
      if (!normalCustodyUserId) {
        alert('We could not tell who has custody on this date. Pick a covered date.');
        return;
      }
      let effectivePickup = pickupTime;
      let effectiveDropoff = dropoffTime;
      if (usesTransitionTimes) {
        if (!transitionType) {
          alert('This date has no scheduled handoff. Time changes apply to handoff days.');
          return;
        }
        if (transitionType === 'pickup') {
          effectiveDropoff = '';
          if (!effectivePickup) {
            alert('Enter the new pickup time.');
            return;
          }
        } else {
          effectivePickup = '';
          if (!effectiveDropoff) {
            alert('Enter the new dropoff time.');
            return;
          }
        }
      } else if (!pickupTime && !dropoffTime) {
        alert('Add a pickup or dropoff time.');
        return;
      }

      createExceptionMutation.mutate({
        assignedUserId: normalCustodyUserId,
        exceptionType: 'TIME_CHANGE',
        pickupTime: effectivePickup,
        dropoffTime: effectiveDropoff,
      });
      return;
    }

    if (!selectedAssignedParent) {
      alert('Please select who should have custody on this date.');
      return;
    }
    if (selectedAssignedParent === normalCustodyUserId) {
      alert('Select the other parent for a custody swap, or switch to "Change times".');
      return;
    }

    createExceptionMutation.mutate({
      assignedUserId: selectedAssignedParent,
      exceptionType: 'SWAP',
      pickupTime,
      dropoffTime,
    });
  };

  const isLoading = householdsQuery.isLoading || schedulesQuery.isLoading || userQuery.isLoading;

  if (isLoading) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (schedulesWithBeneficiaries.length === 0) {
    return (
      <div className='min-h-screen bg-sky-light'>
        <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
          <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-4'>
            <Link to='/app/calendar' className='inline-flex items-center gap-2 text-primary hover:underline'>
              <FiArrowLeft /> Back to Calendar
            </Link>
          </div>
        </header>
        <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-8'>
          <Card className='text-center py-8'>
            <h3
              className='font-bold text-gray-900 mb-2'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              No Custody Schedules
            </h3>
            <p className='text-gray-500 mb-4'>
              Please create a custody schedule first before adding exceptions.
            </p>
            <Link to='/app/calendar/add-schedule'>
              <Button>Add Custody Schedule</Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-sky-light'>
      {/* Header */}
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-4'>
          <Link to='/app/calendar' className='inline-flex items-center gap-2 text-primary hover:underline'>
            <FiArrowLeft /> Back to Calendar
          </Link>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-6'>
        <h1
          className='text-2xl font-bold text-gray-900 mb-6'
          style={{
            fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          Add Exception
        </h1>

        <form onSubmit={handleSubmit} className='space-y-6'>
          {/* Select Schedule */}
          <div>
            <h2
              className='text-lg font-bold text-gray-900 mb-3'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Select Schedule
            </h2>
            <div className='space-y-2'>
              {schedulesWithBeneficiaries.map((schedule) => (
                <button
                  key={schedule.id}
                  type='button'
                  onClick={() => setSelectedSchedule(schedule.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-colors ${
                    selectedSchedule === schedule.id
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-900 border-gray-200 hover:border-primary'
                  }`}
                >
                  <p className='font-bold'>{schedule.beneficiaryName}</p>
                  <p
                    className={`text-sm mt-1 ${
                      selectedSchedule === schedule.id ? 'text-white/80' : 'text-gray-500'
                    }`}
                  >
                    {SCHEDULE_TYPE_NAMES[schedule.schedule_type] ?? schedule.schedule_type}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Exception Date */}
          <div>
            <h2
              className='text-lg font-bold text-gray-900 mb-2'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Exception Date
            </h2>
            <p className='text-sm text-gray-500 mb-3'>Which date should this exception apply to?</p>
            <input
              type='date'
              value={exceptionDate}
              onChange={(e) => setExceptionDate(e.target.value)}
              className='w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
            />
            {selectedSchedule && normalCustodyParent && (
              <p className='text-sm text-gray-500 mt-2 italic'>
                Normally: {getParentName(normalCustodyParent.user_id)} has custody on this date
              </p>
            )}
          </div>

          {/* Request Type */}
          <div>
            <h2
              className='text-lg font-bold text-gray-900 mb-3'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              What do you want to change?
            </h2>
            <div className='grid grid-cols-2 gap-1 bg-gray-100 rounded-xl p-1'>
              <button
                type='button'
                onClick={() => setRequestType('TIME_CHANGE')}
                className={`py-3 rounded-lg font-semibold transition-colors ${
                  requestType === 'TIME_CHANGE'
                    ? 'bg-primary text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Change times
              </button>
              <button
                type='button'
                onClick={() => setRequestType('SWAP')}
                className={`py-3 rounded-lg font-semibold transition-colors ${
                  requestType === 'SWAP'
                    ? 'bg-primary text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Swap custody
              </button>
            </div>
          </div>

          {/* Custody */}
          <div>
            {requestType === 'SWAP' ? (
              <>
                <h2
                  className='text-lg font-bold text-gray-900 mb-2'
                  style={{
                    fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                  }}
                >
                  Who should have custody?
                </h2>
                <p className='text-sm text-gray-500 mb-3'>
                  Select which parent should have custody on this exception date.
                </p>
                <div className='flex flex-wrap gap-2'>
                  {parents.map((parent) => (
                    <button
                      key={parent.user_id}
                      type='button'
                      onClick={() => setSelectedAssignedParent(parent.user_id)}
                      className={`px-4 py-3 rounded-xl border transition-colors font-semibold ${
                        selectedAssignedParent === parent.user_id
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-gray-900 border-gray-200 hover:border-primary'
                      }`}
                    >
                      {getParentName(parent.user_id)}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <h2
                  className='text-lg font-bold text-gray-900 mb-2'
                  style={{
                    fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                  }}
                >
                  Who has custody?
                </h2>
                {normalCustodyParent ? (
                  <p className='text-sm text-gray-500 mb-3'>
                    {getParentName(normalCustodyParent.user_id)} keeps custody — only the handoff
                    time changes.
                  </p>
                ) : (
                  <p className='text-sm text-gray-500 mb-3'>
                    Pick a date covered by the schedule to set a time change.
                  </p>
                )}
                {usesTransitionTimes && !transitionType && (
                  <p className='text-sm text-amber-700'>
                    This date has no scheduled handoff. Choose a handoff day to change its time.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Reason (Optional) */}
          <div>
            <h2
              className='text-lg font-bold text-gray-900 mb-2'
              style={{
                fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Reason (Optional)
            </h2>
            <p className='text-sm text-gray-500 mb-3'>
              Add a reason for this schedule change request.
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder='e.g., Special event, vacation, etc.'
              rows={3}
              className='w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none'
            />
          </div>

          {/* Pickup Time */}
          {(requestType === 'SWAP' ||
            !usesTransitionTimes ||
            transitionType === 'pickup') && (
            <div>
              <h2
                className='text-lg font-bold text-gray-900 mb-2'
                style={{
                  fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
              >
                {requestType === 'TIME_CHANGE' ? 'New Pickup Time' : 'Pickup Time (Optional)'}
              </h2>
              <p className='text-sm text-gray-500 mb-3'>
                {requestType === 'TIME_CHANGE'
                  ? 'Set the new pickup time for this date.'
                  : 'Override pickup time for this exception date.'}
              </p>
              <div className='flex items-center gap-3'>
                <input
                  type='time'
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                  className='flex-1 px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
                />
                {pickupTime && (
                  <button
                    type='button'
                    onClick={() => setPickupTime('')}
                    className='px-3 py-2 text-red-600 hover:text-red-700 font-semibold'
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Dropoff Time */}
          {(requestType === 'SWAP' ||
            !usesTransitionTimes ||
            transitionType === 'dropoff') && (
            <div>
              <h2
                className='text-lg font-bold text-gray-900 mb-2'
                style={{
                  fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
              >
                {requestType === 'TIME_CHANGE' ? 'New Dropoff Time' : 'Dropoff Time (Optional)'}
              </h2>
              <p className='text-sm text-gray-500 mb-3'>
                {requestType === 'TIME_CHANGE'
                  ? 'Set the new dropoff time for this date.'
                  : 'Override dropoff time for this exception date.'}
              </p>
              <div className='flex items-center gap-3'>
                <input
                  type='time'
                  value={dropoffTime}
                  onChange={(e) => setDropoffTime(e.target.value)}
                  className='flex-1 px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all'
                />
                {dropoffTime && (
                  <button
                    type='button'
                    onClick={() => setDropoffTime('')}
                    className='px-3 py-2 text-red-600 hover:text-red-700 font-semibold'
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Submit */}
          <Button
            type='submit'
            fullWidth
            disabled={
              createExceptionMutation.isPending ||
              !selectedSchedule ||
              (requestType === 'SWAP' ? !selectedAssignedParent : !normalCustodyUserId)
            }
            loading={createExceptionMutation.isPending}
          >
            {requestType === 'TIME_CHANGE' ? 'Request Time Change' : 'Request Swap'}
          </Button>
        </form>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/calendar/add-exception')({
  component: AddExceptionPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      date: typeof search.date === 'string' ? search.date : undefined,
    };
  },
});
